import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './app/routes';
import { AuthProvider } from './lib/AuthProvider';
import { UpdatePrompt } from './components/UpdatePrompt';
import { isTeamsTabPath } from './lib/teamsTab';
import { initTheme } from '@shared/theme';

// Monaco init is NOT imported here: pulling `monaco-editor` into the entry
// chunk added ~megabytes that only the (lazy-loaded) designer needs. It now
// lives next to its consumers — CodeEditor.tsx / VersionHistoryPanel.tsx import
// './lib/monaco' so the loader config runs before any <Editor> mounts, and
// Monaco rides along in the Designer chunk instead of the entry bundle.

// KendoReact theme. The Kendo Fluent base theme provides the component
// structure; @shared/theme then maps its --kendo-* CSS variables onto the
// CloudRadial design tokens (docs/design/automations-design.html). Order is
// load-bearing: the shared cascade MUST come after the Fluent base so its
// :root overrides win. See src/frontend/shared/src/theme/index.css.
import '@progress/kendo-theme-fluent/dist/all.css';
import '@shared/theme/index.css';

import './index.css';

// Sync the theme into React state and apply density. The initial theme is
// already applied pre-paint by index.html's inline script (reading the
// crauto-theme cookie); this defaults to light to keep parity with the
// existing shell + login — the design's canonical mode is dark, one toggle
// away (applyTheme('dark')).
initTheme('light');

// KendoReact licensing is activated at build time via `npx kendo-ui-license
// activate` (TELERIK_LICENSE), which bakes the key into @progress/kendo-licensing.
// No runtime activation call is needed. See docs/architecture.md (Phase 5).

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
      {/* Registers the service worker (so the app is installable from any
          route, including /login) and shows the "new version" reload toast.
          Mounted at the root, outside the router, so it is route-agnostic —
          except inside the Teams tab, which must never be served by one. */}
      {!isTeamsTabPath(window.location.pathname) && <UpdatePrompt />}
    </AuthProvider>
  </StrictMode>,
);
